import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Send } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { Reveal } from "../components/Motion";
import { api } from "../lib/api";
import { PRODUCTS } from "../lib/data";
import { SEO_LANDING_PAGES } from "../lib/seoLandingA";
import { SEO_LANDING_PAGES_B } from "../lib/seoLandingB";

const ALL = { ...SEO_LANDING_PAGES, ...SEO_LANDING_PAGES_B };

function bolded(text) {
    return text.split("**").map((c, i) => i % 2 === 1 ? <strong key={i} className="text-forest">{c}</strong> : c);
}

export default function SeoLandingPage() {
    const { slug: paramSlug } = useParams();
    // Support both /some-slug and route-defined pages
    const slug = paramSlug || window.location.pathname.replace(/^\//, "");
    const cfg = ALL[slug];
    if (!cfg) return <Navigate to="/" replace />;

    const canonical = `https://thepuropack.com/${slug}`;
    const schema = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Home", item: "https://thepuropack.com/" },
                    { "@type": "ListItem", position: 2, name: cfg.h1, item: canonical },
                ],
            },
            {
                "@type": "FAQPage",
                mainEntity: cfg.faqs.map(f => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            },
        ],
    };

    return (
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10" data-testid={`seo-${slug}`}>
            <Seo title={cfg.seoTitle.replace(" | The Puro Pack", "").replace(" | Puro Pack", "")} description={cfg.metaDescription} path={`/${slug}`} canonicalOverride={cfg.canonicalTo ? `https://thepuropack.com${cfg.canonicalTo}` : undefined} image={cfg.images?.[0]} schema={schema} />
            <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: cfg.h1, to: `/${slug}` }]} />

            {/* Hero */}
            <section className="pt-14 pb-16 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7">
                    <div className="font-mono text-xs uppercase tracking-widest text-clay mb-6">{cfg.eyebrow}</div>
                    <h1 className="font-serif text-5xl lg:text-7xl leading-[0.98] tracking-tight text-forest">{cfg.h1}</h1>
                    <p className="mt-8 text-inkgreen/85 text-lg leading-relaxed max-w-2xl">{bolded(cfg.intro)}</p>
                    <div className="mt-8 flex gap-3 flex-wrap">
                        <Link to="/contact" className="btn-pill btn-solid" data-testid="seo-request-sample">Request a Sample <ArrowUpRight className="w-4 h-4" /></Link>
                        <a href="#enquiry-form" className="btn-pill" data-testid="seo-get-quote">Get a Quote →</a>
                    </div>
                </div>
                <div className="lg:col-span-5 grid grid-cols-2 gap-4">
                    {(cfg.images || []).slice(0, 2).map((src, i) => (
                        <div key={i} className={`image-frame ${i === 0 ? "row-span-2 aspect-[3/4]" : "aspect-square"}`}>
                            <img src={src} alt={`${cfg.h1} — The Puro Pack`} loading="lazy" />
                        </div>
                    ))}
                </div>
            </section>

            {/* Sections */}
            <section className="pb-16 border-t border-forest/30">
                {cfg.sections.map((sec, i) => (
                    <Reveal key={i} delay={i * 0.04}>
                        <div className="grid lg:grid-cols-12 gap-8 py-10 border-b border-forest/20">
                            <div className="lg:col-span-4 font-mono text-4xl lg:text-5xl text-clay">[0{i + 1}]</div>
                            <div className="lg:col-span-8">
                                <h2 className="font-serif text-3xl lg:text-4xl text-forest tracking-tight mb-4">{sec.h2}</h2>
                                <p className="text-inkgreen/80 leading-relaxed text-base lg:text-lg max-w-3xl">{sec.body}</p>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </section>

            {/* Product cross-links */}
            <section className="py-16">
                <Reveal><h2 className="font-serif text-4xl lg:text-5xl text-forest tracking-tight mb-8">Related products</h2></Reveal>
                <div className="grid md:grid-cols-3 gap-6">
                    {PRODUCTS.map((p) => (
                        <Link key={p.slug} to={`/products/${p.slug}`} className="group border border-forest/30 hover:border-forest transition-colors block">
                            <div className="image-frame aspect-[4/3]"><img src={p.image} alt={p.title} loading="lazy" /></div>
                            <div className="p-5 flex justify-between items-center">
                                <div>
                                    <h3 className="font-serif text-2xl text-forest">{p.title}</h3>
                                    <div className="text-xs font-mono uppercase text-stone mt-1">{p.highlights[0].value}</div>
                                </div>
                                <ArrowUpRight className="text-forest group-hover:text-clay" />
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* FAQ */}
            <section className="border-t border-forest/30 py-16" data-testid="seo-faq">
                <Reveal>
                    <h2 className="font-serif text-4xl lg:text-5xl text-forest tracking-tight mb-10">Frequently asked questions</h2>
                </Reveal>
                <div className="max-w-4xl">
                    {cfg.faqs.map((f, i) => (
                        <details key={i} className="border-b border-forest/20 py-4 group" data-testid={`seo-faq-${i}`}>
                            <summary className="cursor-pointer font-serif text-xl md:text-2xl text-forest list-none flex justify-between items-start gap-4">
                                <span>{f.q}</span>
                                <span className="font-mono text-clay text-sm group-open:rotate-45 transition-transform">+</span>
                            </summary>
                            <p className="mt-3 text-inkgreen/80 leading-relaxed max-w-3xl">{f.a}</p>
                        </details>
                    ))}
                </div>
            </section>

            {/* Enquiry form */}
            <EnquiryForm slug={slug} pageTitle={cfg.h1} />

            {/* Internal cross-links */}
            <section className="border-t border-forest/30 py-16">
                <div className="font-mono text-xs uppercase tracking-widest text-stone mb-6">[ You may also read ]</div>
                <div className="grid md:grid-cols-3 gap-4">
                    {Object.entries(ALL).filter(([s]) => s !== slug).slice(0, 6).map(([s, p]) => (
                        <Link key={s} to={`/${s}`} className="border border-forest/20 hover:border-forest hover:bg-sand transition-colors p-5 group">
                            <div className="font-serif text-lg text-forest group-hover:text-clay">{p.h1}</div>
                            <div className="mt-2 text-xs font-mono uppercase text-stone">Read more →</div>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}

function EnquiryForm({ slug, pageTitle }) {
    const [form, setForm] = useState({ company_name: "", contact_person: "", email: "", mobile: "", country: "India", monthly_requirement: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    async function submit(e) {
        e.preventDefault();
        for (const k of ["company_name", "contact_person", "email", "mobile", "monthly_requirement"]) {
            if (!form[k]) { toast.error(`Please fill in ${k.replace(/_/g, " ")}`); return; }
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            fd.append("business_type", "Other");
            fd.append("products_interested", pageTitle);
            fd.append("message", `Enquiry from /${slug}`);
            await api.post("/contact", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setDone(true);
            toast.success("Enquiry received — we'll respond within 24 hours.");
        } catch (_e) { toast.error("Something went wrong. Please try again."); }
        finally { setSubmitting(false); }
    }

    return (
        <section id="enquiry-form" className="border-t border-forest/30 py-16" data-testid="seo-enquiry-form">
            <div className="grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-5">
                    <div className="font-mono text-xs uppercase tracking-widest text-clay mb-4">[ Get a quote ]</div>
                    <h2 className="font-serif text-4xl lg:text-5xl text-forest tracking-tight">Talk to production.</h2>
                    <p className="mt-4 text-inkgreen/80 max-w-md">Tell us about your product, volume and timeline. We respond within 24 hours.</p>
                </div>
                <div className="lg:col-span-7">
                    {done ? (
                        <div className="border border-forest p-10" data-testid="seo-form-success">
                            <h3 className="font-serif text-3xl text-forest">Thank you.</h3>
                            <p className="mt-3 text-inkgreen/80">We&apos;ve received your enquiry. Our team will respond within 24 hours.</p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="grid md:grid-cols-2 gap-6">
                            <div><label className="font-mono text-xs uppercase text-stone">Company *</label><input required className="print-input" data-testid="seo-form-company" value={form.company_name} onChange={u("company_name")} /></div>
                            <div><label className="font-mono text-xs uppercase text-stone">Contact person *</label><input required className="print-input" data-testid="seo-form-contact" value={form.contact_person} onChange={u("contact_person")} /></div>
                            <div><label className="font-mono text-xs uppercase text-stone">Email *</label><input required type="email" className="print-input" data-testid="seo-form-email" value={form.email} onChange={u("email")} /></div>
                            <div><label className="font-mono text-xs uppercase text-stone">Mobile *</label><input required className="print-input" data-testid="seo-form-mobile" value={form.mobile} onChange={u("mobile")} /></div>
                            <div><label className="font-mono text-xs uppercase text-stone">Country</label><input className="print-input" data-testid="seo-form-country" value={form.country} onChange={u("country")} /></div>
                            <div><label className="font-mono text-xs uppercase text-stone">Monthly requirement *</label><input required className="print-input" data-testid="seo-form-monthly" value={form.monthly_requirement} onChange={u("monthly_requirement")} placeholder="e.g. 50,000 units/month" /></div>
                            <div className="md:col-span-2 pt-3">
                                <button type="submit" disabled={submitting} className="btn-pill btn-solid" data-testid="seo-form-submit">
                                    {submitting ? "Sending…" : <>Submit enquiry <Send className="w-4 h-4" /></>}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}
